import React, { useState, useEffect } from "react";
import PayoutAttempts from "../../../components/PayoutAttempts";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Search, MoreVertical, X, Copy, Loader2, Loader } from "lucide-react";
import Dropdown from "../../../components/ui/Dropdown";
import Pagination from "../../../components/ui/Pagination";
// import { format } from "date-fns";

import { toast } from "react-hot-toast";
import { usePendingPayout, useRetryPendingPayout, useDeclinePayout, useMarkPayoutSuccessful } from "../hooks/usePendingPayout";
import DeclineReasonModal from "../../../components/ui/DeclineReasonModal";
import SuccessNoteModal from "../../../components/ui/SuccessNoteModal";

// details modal
const DetailsModal = ({ isOpen, onClose, data, onReject, onSuccessAction, setLoadingRowId }) => {
  const { mutate: retryPendingPayout, isPending: isRetrying } = useRetryPendingPayout();

  if (!isOpen || !data) return null;

  const detailItem = (label, value) => (
    <div className="flex flex-col mb-4">
      <span className="text-xs tracking-wider text-gray-500 uppercase">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value || "N/A"}</span>
    </div>
  );

  const formatNumber = (val) => {
    const num = Number(val);
    return isNaN(num) ? "0.00" : num.toLocaleString(undefined, { minimumFractionDigits: 2 });
  };

  const symbol = data.currency?.symbol || "";

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50 transition-opacity">
      <div className="overflow-hidden h-[90vh] mx-4 w-full max-w-2xl bg-white rounded-2xl shadow-2xl transition-all transform">
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Payout Details</h3>
          <button onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-8 max-h-[80%] overflow-y-auto">
          <div className="grid grid-cols-1 gap-y-2 gap-x-8 md:grid-cols-2">
            <div className="col-span-2 mb-6">
              <h4 className="pb-2 mb-4 text-sm font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">Transaction Information</h4>
              <div className="grid grid-cols-2 gap-4">
                {detailItem("Transaction ID", data.uuid)}
                {detailItem("Reference", data.providerReference)}
                {detailItem("Status", data.status?.toUpperCase())}
                {detailItem("Date", data.createdAt ? new Date(data.createdAt).toLocaleString() : "N/A")}
                {detailItem("Amount", `${symbol} ${formatNumber(data.amount)}`)}
                {detailItem("Provider Fee", `${symbol} ${formatNumber(data.providerFee)}`)}
                {detailItem("Platform Fee", `${symbol} ${formatNumber(data.platformFee)}`)}
                {detailItem("Total Amount", `${symbol} ${formatNumber(data.totalAmount)}`)}
              </div>
            </div>

            <div className="col-span-2 mb-6">
              <h4 className="pb-2 mb-4 text-sm font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">User Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid grid-cols-1 gap-4">
                  {detailItem("Full Name", `${data.user?.firstName || ""} ${data.user?.lastName || ""}`)}
                  {detailItem("Email", data.user?.email)}
                </div>
                {detailItem("Phone", data.user?.phoneNumber)}
                {detailItem("Country", data.user?.country)}
                {detailItem("Account Active", data.user?.isActive ? "YES" : "NO")}
                {detailItem("KYC Completed", data.user?.hasCompletedKYC ? "YES" : "NO")}
              </div>
            </div>

            <div className="col-span-2 mb-6">
              <h4 className="pb-2 mb-4 text-sm font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">Currency Info</h4>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                {detailItem("Code", data.currency?.currencyCode)}
                {detailItem("Symbol", data.currency?.symbol)}
                {detailItem("Country", data.currency?.country)}
              </div>
            </div>

            <div className="col-span-2 mb-6">
              <h4 className="pb-2 mb-4 text-sm font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">Payout Method</h4>
              <div className="grid grid-cols-2 gap-4">
                {detailItem("Name", data.payoutMethod?.name)}
                {detailItem("Fee Type", data.payoutMethod?.payoutFeeType)}
                {detailItem("Fee Value", data.payoutMethod?.payoutFeeValue)}
                <div className="col-span-2">
                  {detailItem("Description", data.payoutMethod?.description)}
                </div>
              </div>
            </div>

            <div className="col-span-2 mb-6">
              <h4 className="pb-2 mb-4 text-sm font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">Beneficiary Details</h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {detailItem("Beneficiary Name", data.beneficiary?.name || data.beneficiary?.display_name)}
                {detailItem("Bank Name", data.beneficiary?.metadata?.bank_name)}

                {data.beneficiary?.metadata?.account_number && <div>
                  {detailItem("Account Number", data.beneficiary?.metadata?.account_number)}
                </div>}

                {data.beneficiary?.metadata?.phone_number && <div>
                  {detailItem("Phone Number", data.beneficiary?.metadata?.phone_number)}
                </div>}

                {data.beneficiary?.metadata?.bank_code && <div>
                  {detailItem("Bank Code", data.beneficiary?.metadata?.bank_code)}
                </div>}

                {data.beneficiary?.metadata?.crypto && <div>
                  {detailItem("Crypto", data.beneficiary?.metadata?.crypto)}
                </div>}

                {data.beneficiary?.metadata?.network && <div>
                  {detailItem("Network", data.beneficiary?.metadata?.network)}
                </div>}

                {data.beneficiary?.metadata?.wallet_address && <div>
                  {detailItem("Wallet Address", data.beneficiary?.metadata?.wallet_address)}
                </div>}
              </div>
            </div>

            <div className="col-span-2">
              <PayoutAttempts payout={data} />
            </div>

            <div className="col-span-2 mt-1">
              <span className="block pb-2 mb-2 text-xs font-bold text-emerald-800 uppercase border-b border-emerald-100">Description</span>
              <p className="text-sm italic leading-relaxed text-gray-700">
                {data.description || "No description provided"}
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-center items-center px-2 py-6 space-x-2 space-y-0 bg-gray-50 border-t md:justify-end md:space-y-0 md:space-x-4">
          <button
            onClick={onClose}
            className="hidden p-2 font-bold text-gray-600 bg-white rounded-xl border-2 border-gray-200 shadow-sm transition-all md:block md:py-3 md:px-5 md:text-sm hover:bg-gray-50 active:scale-95"
          >
            Close
          </button>

          <button
            onClick={() => onReject(data)}
            className="p-2 text-xs font-bold text-red-600 bg-white rounded-xl border-2 border-red-500 shadow-sm transition-all md:py-3 md:px-5 md:text-sm hover:bg-red-50 active:scale-95"
          >
            Decline Payout
          </button>

          <button
            onClick={() => onSuccessAction(data)}
            className="p-2 text-xs font-bold text-white bg-emerald-600 rounded-xl border-2 border-emerald-500 shadow-sm transition-all md:py-3 md:px-5 md:text-sm hover:bg-emerald-50 active:scale-95"
          >
            Mark Successful
          </button>

          <button
            onClick={() => {
              setLoadingRowId(data?.uuid);
              retryPendingPayout(data?.uuid, {
                onSuccess: () => {
                  toast.success("Payout retry initiated");
                  onClose();
                  setLoadingRowId(null);
                },
                onError: (err) => {
                  toast.error(err?.response?.data?.message || "Retry failed");
                  setLoadingRowId(null);
                }
              });
            }}
            disabled={isRetrying}
            className={`flex text-xs justify-center items-center p-2 md:py-3 md:px-5 font-bold text-emerald-600 bg-white rounded-xl border-2 border-emerald-500 shadow-lg  md:text-sm transition-all hover:bg-emerald-50 active:scale-95 ${isRetrying ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {isRetrying ? "Retrying..." : "Retry Payout"}
          </button>
        </div>
      </div>
    </div>
  );
};

export function PendingPayoutTable() {
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successTarget, setSuccessTarget] = useState(null);

  const [loadingRowId, setLoadingRowId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilter(globalFilter);
      if (globalFilter !== debouncedFilter) {
        setPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [globalFilter, debouncedFilter]);

  const { data, isLoading, isError, error } = usePendingPayout(page, limit, debouncedFilter);
  const { mutate: retryPendingPayout } = useRetryPendingPayout();
  const { mutate: declinePayout, isPending: isDeclining } = useDeclinePayout();
  const { mutate: markPayoutSuccessful, isPending: isMarkingSuccess } = useMarkPayoutSuccessful();


  const handleViewDetails = (payout) => {
    setSelectedPayout(payout);
    setIsDetailsModalOpen(true);
  };

  const handleOpenDecline = (payout) => {
    setDeclineTarget(payout);
    setIsDeclineModalOpen(true);
  };

  const handleOpenSuccess = (payout) => {
    setSuccessTarget(payout);
    setIsSuccessModalOpen(true);
  };

  const handleConfirmSuccess = ({ note, providerReference }) => {
    if (!successTarget) return;

    setLoadingRowId(successTarget.uuid);
    markPayoutSuccessful(
      {
        payoutUuid: successTarget.uuid,
        providerReference: providerReference,
        note: note,
      },
      {
        onSuccess: () => {
          toast.success("Payout marked as successful");
          setIsSuccessModalOpen(false);
          setIsDetailsModalOpen(false);
          setSuccessTarget(null);
          setLoadingRowId(null);
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Failed to mark payout successful");
          setLoadingRowId(null);
        },
      }
    );
  };

  const handleConfirmDecline = (reason) => {
    if (!declineTarget) return;

    setLoadingRowId(declineTarget.uuid);
    declinePayout(
      {
        payoutUuid: declineTarget?.uuid,
        reason: reason,
      },
      {
        onSuccess: () => {
          toast.success("Payout declined successfully");
          setIsDeclineModalOpen(false);
          setIsDetailsModalOpen(false);
          setDeclineTarget(null);
          setLoadingRowId(null);
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Failed to decline payout");
          setLoadingRowId(null);
        },
      }
    );
  };

  const truncateText = (text, length = 10) => {
    if (!text) return "";
    return text.length > length
      ? `${text.slice(0, length)}...`
      : text;
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const columns = [
    {
      id: "uuid",
      accessorKey: "uuid",
      header: "Transaction ID",
      cell: ({ getValue }) => {
        const value = getValue();

        return (
          <div className="flex gap-1 justify-between items-center min-w-[130px]">
            <span title={value}>
              {truncateText(value, 12)}
            </span>

            <button
              onClick={() => handleCopy(value)}
              className="text-gray-500 hover:text-black"
            >
              <Copy size={14} />
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: "providerReference",
      header: "Reference",
      cell: ({ getValue }) => {
        const value = getValue();

        return (
          <div className="flex gap-2 justify-between items-center min-w-[130px]">
            <span title={value || "---"}>
              {truncateText(value || "---", 8)}
            </span>

            <button
              onClick={() => handleCopy(value)}
              className="text-gray-500 hover:text-black"
            >
              <Copy size={14} />
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: "Date",
      cell: (info) => info.getValue() ? new Date(info.getValue()).toLocaleString() : "N/A"
    },
    {
      id: "user",
      header: "User",
      accessorFn: (row) => `${row.user?.firstName || ""} ${row.user?.lastName || ""}`,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">
            {row.original.user?.firstName} {row.original.user?.lastName}
          </span>
          <span className="text-xs text-gray-500">{row.original.user?.email}</span>
        </div>
      ),
    },
    {
      id: "amount",
      header: "Amount",
      accessorFn: (row) => `${row.currency?.symbol || ""}${row.amount}`,
      cell: ({ row }) => (
        <span className="font-semibold whitespace-nowrap">
          {row.original.currency?.symbol || ""}{" "}
          {Number(row.original.amount || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      id: "totalAmount",
      header: "Total Amount",
      accessorFn: (row) => `${row.currency?.symbol || ""}${row.totalAmount}`,
      cell: ({ row }) => (
        <span className="text-gray-600 whitespace-nowrap">
          {row.original.currency?.symbol || ""} {Number(row.original.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => {
        const status = info.getValue();

        const statusStyles = {
          manual_review: "bg-yellow-100 text-yellow-800",
          approved: "bg-green-100 text-green-800",
          rejected: "bg-red-100 text-red-800",
          refunded: "bg-blue-100 text-blue-800",
          pending: "bg-yellow-100 text-yellow-800",
          failed: "bg-red-100 text-red-800",
        };

        return (
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusStyles[status] || "bg-gray-100 text-gray-800"
              }`}
          >
            {status?.replace("_", " ").toUpperCase()}
          </span>
        );
      }
    },
    {
      id: "metadata",
      header: "Last Retry Error",
      accessorFn: (row) => `${row.metadata?.last_retry_error || ""}`,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="truncate">
            {row.original.metadata?.last_retry_error}
          </span>
          <span className="text-xs text-gray-500">{row.original.metadata?.last_retry_at ? new Date(row.original.metadata?.last_retry_at).toLocaleString() : "N/A"}</span>
        </div>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const payout = row.original;
        const isRowLoading = loadingRowId === payout.uuid;

        const dropdownItems = [
          {
            label: "View Details",
            onClick: () => handleViewDetails(payout),
          },
          {
            label: "Mark Successful",
            onClick: () => handleOpenSuccess(payout),
            className: "text-emerald-600",
          },
          {
            label: isRowLoading ? "Retrying..." : "Retry Payout",
            onClick: () => {
              setLoadingRowId(payout?.uuid);
              retryPendingPayout(payout?.uuid, {
                onSuccess: () => {
                  toast.success("Payout retry initiated");
                  setLoadingRowId(null);
                },
                onError: (err) => {
                  toast.error(err?.response?.data?.message || "Retry failed");
                  setLoadingRowId(null);
                }
              });
            },
            className: "text-emerald-600",
          },
          {
            label: "Decline Payout",
            onClick: () => handleOpenDecline(payout),
            className: "text-red-600",
          },

        ];

        return (
          <Dropdown
            trigger={isRowLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreVertical className="w-4 h-4" />}
            items={dropdownItems}
          />
        );
      },
    },
  ];

  const tableData = React.useMemo(() => {
    if (!data) return [];
    return Array.isArray(data) ? data : data.data || [];
  }, [data]);

  const totalPages = data?.meta?.totalPages || data?.pagination?.totalPages || 1;

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: totalPages,
    initialState: {
      pagination: {
        pageSize: limit,
        pageIndex: page - 1,
      },
    },
  });

  const handleSearch = (e) => {
    const value = e.target.value;
    setGlobalFilter(value);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // if (isLoading) {
  //   return (
  //     <div className="flex h-screen">
  //       <div className="flex flex-1 justify-center items-center">
  //         <p className="font-medium text-gray-600">Loading payouts data...</p>
  //       </div>
  //     </div>
  //   );
  // }

  if (isError) {
    return (
      <div className="flex h-screen">
        <div className="flex flex-1 justify-center items-center">
          <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
            Error: {error.message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="flex justify-between items-center pb-5 bg-white">
        <div className="relative w-80">
          <Search className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by ref, name or email..."
            className="py-3 pr-4 pl-12 w-full text-base rounded-3xl border border-gray-200 transition-all outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            value={globalFilter}
            onChange={handleSearch}
          />
        </div>
      </div>

      {
        isLoading ? (
          <div className="flex h-[400px]">
            <div className="flex flex-1 justify-center items-center">
              <p className="flex gap-2 items-center font-medium text-gray-600">Loading pending payouts data <Loader className="w-5 h-5 animate-spin" /></p>
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded border border-gray-200">
            <div className="overflow-x-auto min-h-[400px]">
              <table className="w-full">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="bg-gray-50/50">
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={`px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6 ${header.id === "metadata" || header.id === "totalAmount" ? "min-w-[200px]" : ""}`}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-gray-100 hover:border-b">
                  {tableData.length > 0 ? (
                    table.getRowModel().rows.map((row) => (
                      <tr key={row.id} className="transition-colors hover:bg-gray-50">
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className="px-4 py-3 font-sans text-sm text-gray-600 md:px-6 md:py-5"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="px-6 py-12 text-center text-gray-500"
                      >
                        <div className="flex flex-col items-center">
                          <p className="text-lg font-medium">No records matching your search</p>
                          <p className="text-sm">Try adjusting your filters or search terms</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* totalPages > 1  */}
            <div className="flex flex-col gap-4 justify-between items-center p-3 border-t border-gray-200 md:p-6 bg-gray-50/50 md:flex-row">
              <div className="text-sm text-gray-600">
                Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{" "}
                <span className="font-medium">
                  {Math.min(page * limit, data?.meta?.totalItems || data?.pagination?.totalItems || 0)}
                </span>{" "}
                of <span className="font-medium">{data?.meta?.totalItems || data?.pagination?.totalItems || 0}</span> entries
              </div>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        )
      }

      <DetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        data={selectedPayout}
        onReject={handleOpenDecline}
        onSuccessAction={handleOpenSuccess}
        setLoadingRowId={setLoadingRowId}
      />

      <DeclineReasonModal
        isOpen={isDeclineModalOpen}
        onClose={() => setIsDeclineModalOpen(false)}
        onSubmit={handleConfirmDecline}
        isLoading={isDeclining}
        title="Decline Payout"
        data={declineTarget}
      />

      <SuccessNoteModal
        isOpen={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
        onSubmit={handleConfirmSuccess}
        isLoading={isMarkingSuccess}
        data={successTarget}
      // initialProviderReference={successTarget?.providerReference || ""}
      />
    </div>
  );
}

